import type { FilterOption, FilterOptionConfig } from '@brightdrop/bd-ui';

import type { BDFilterModalOptionConfig } from '~/common/components/BDFilter/BDFilterModal';
import { translateText } from '~/common/components/TranslatedLabel/TranslatedLabel';
import {
  AssetServiceStatus,
  LocationMaskingStatus,
} from '~/common/models/asset.model';
import {
  BatterySocStatus,
  BrakePadStatus,
  FuelStatus,
  LastCommunicatedStatus,
  LastIgnitionStatus,
  OilLifeStatus,
  ReportDiagnosticState,
  VehiclePropulsion,
} from '~/common/models/asset-report.model';
import { ConnectivityStatusType } from '~/common/models/common.model';
import { getVehicleHealthTitleKey } from '~/common/utils/vehicleHealthCopy';

import type { AssetListFilterCriteria } from '../../assetsSlice';
import {
  BRAKE_PAD_FILTER_MODAL_STATUS,
  FUEL_CHARGE_FILTER_MODAL_STATUS,
  OIL_LIFE_FILTER_MODAL_STATUS,
} from '../AssetReportView.constant';
import {
  getDiagnosticModalLabel,
  getDiagnosticsFilterModalLabel,
} from '../utils/report.utils';

export const getConnectivityBoardingStatusConfig = (
  isNotEquippedFeatureFlag: boolean
): FilterOption<ConnectivityStatusType>[] => {
  const availableOptions: ConnectivityStatusType[] = [
    isNotEquippedFeatureFlag
      ? ConnectivityStatusType.NOT_EQUIPPED
      : ConnectivityStatusType.VERIFIED,
    ConnectivityStatusType.VERIFICATION_PENDING,
    ConnectivityStatusType.CONNECTION_PENDING,
    ConnectivityStatusType.CONNECTED,
  ];

  return availableOptions.map((status) => {
    return {
      label: translateText(
        `asset:filterModalLabels.connectivityBoardingStatus.${status}`
      ),
      value: status,
    };
  });
};

export const getLocationMaskingStatusConfig =
  (): FilterOption<LocationMaskingStatus>[] => {
    const availableOptions = [
      LocationMaskingStatus.DISABLED,
      LocationMaskingStatus.ENABLED,
    ];
    return availableOptions.map((status) => {
      return {
        label: translateText(
          `asset:filterModalLabels.locationMaskingStatus.${status}`
        ),
        value: status,
      };
    });
  };

const locationMaskingStatusFilter = () =>
  ({
    title: translateText('asset:locationSharing'),
    id: 'location-masking-status',
    type: 'locationMaskingStatus',
    options: getLocationMaskingStatusConfig(),
  }) as FilterOptionConfig<LocationMaskingStatus, AssetListFilterCriteria>;

const connectivityBoardingStatusFilter = (isNotEquippedFeatureFlag: boolean) =>
  ({
    title: translateText('common:status'),
    id: 'connectivity-boarding-status',
    type: 'connectivityBoardingStatus',
    options: getConnectivityBoardingStatusConfig(isNotEquippedFeatureFlag),
  }) as FilterOptionConfig<ConnectivityStatusType, AssetListFilterCriteria>;

export const getOverviewFilterModalConfig = (
  isNotEquippedFeatureFlag: boolean,
  isVehicleListLocationMaskingEnabled?: boolean,
  isMarchVehicleManagementEnabled?: boolean
): BDFilterModalOptionConfig<AssetListFilterCriteria>[] => {
  return [
    connectivityBoardingStatusFilter(isNotEquippedFeatureFlag),
    locationMaskingStatusFilter(),
    ...(isMarchVehicleManagementEnabled
      ? [lastIgnitionStatusFilter()]
      : [connectivityStatusFilter()]),
  ].filter((filter) => {
    if (
      filter.id === 'location-masking-status' &&
      !isVehicleListLocationMaskingEnabled
    ) {
      return false;
    }
    return true;
  });
};

export const AVAILABLE_DIAGNOSTICS_HEALTH_OPTIONS = [
  ReportDiagnosticState.CRITICAL,
  ReportDiagnosticState.IMPAIRED,
  ReportDiagnosticState.OK,
];

export const getDiagnosticsConfig = (): FilterOption<ReportDiagnosticState>[] =>
  AVAILABLE_DIAGNOSTICS_HEALTH_OPTIONS.map((diagnosticStatus) => {
    return {
      label: translateText(
        `asset:filterModalLabels.diagnostics.${diagnosticStatus}`
      ),
      value: diagnosticStatus,
    };
  });

const diagnosticsFilter = (isVehicleHealthEnabled: boolean) =>
  ({
    title: translateText(getVehicleHealthTitleKey(isVehicleHealthEnabled)),
    id: 'diagnostics-health',
    type: 'diagnosticsHealthStatus',
    options: getDiagnosticsConfig(),
  }) as FilterOptionConfig<ReportDiagnosticState, AssetListFilterCriteria>;

export const AVAILABLE_AVAILABILITY_OPTIONS = [
  AssetServiceStatus.IN_SERVICE,
  AssetServiceStatus.OUT_OF_SERVICE,
];

export const getAvailabilityConfig = (): FilterOption<AssetServiceStatus>[] =>
  AVAILABLE_AVAILABILITY_OPTIONS.map((availabilityStatus) => {
    return {
      label: translateText(`asset:serviceStatus.${availabilityStatus}`),
      value: availabilityStatus,
    };
  });

export const AVAILABLE_FUEL_FILTER_OPTIONS = Object.values(FuelStatus);

export const getFuelConfig = (): FilterOption<FuelStatus>[] =>
  AVAILABLE_FUEL_FILTER_OPTIONS.map((status) => {
    return {
      label: getDiagnosticsFilterModalLabel(
        FUEL_CHARGE_FILTER_MODAL_STATUS,
        status
      ),
      value: status,
    };
  });

const fuelFilter = () =>
  ({
    title: translateText('asset:fuel'),
    id: 'fuel-status',
    type: 'fuelStatus',
    options: getFuelConfig(),
  }) as FilterOptionConfig<FuelStatus, AssetListFilterCriteria>;

export const AVAILABLE_BATTERY_SOC_FILTER_OPTIONS =
  Object.values(BatterySocStatus);

export const getBatterySocConfig = (): FilterOption<BatterySocStatus>[] =>
  AVAILABLE_BATTERY_SOC_FILTER_OPTIONS.map((status) => {
    return {
      label: getDiagnosticsFilterModalLabel(
        FUEL_CHARGE_FILTER_MODAL_STATUS,
        status
      ),
      value: status,
    };
  });

const batterySocFilter = () =>
  ({
    title: translateText('asset:tableColumnLabels.batterySocStatus'),
    id: 'battery-soc-status',
    type: 'batterySocStatus',
    options: getBatterySocConfig(),
  }) as FilterOptionConfig<BatterySocStatus, AssetListFilterCriteria>;

export const AVAILABLE_TIRE_FILTER_OPTIONS = [
  ReportDiagnosticState.GOOD,
  ReportDiagnosticState.CRITICAL,
  ReportDiagnosticState.IMPAIRED,
];

export const getTireConfig = (): FilterOption<ReportDiagnosticState>[] =>
  AVAILABLE_TIRE_FILTER_OPTIONS.map((tireStatus) => {
    return {
      label: translateText(`asset:filterModalLabels.tireStatus.${tireStatus}`),
      value: tireStatus,
    };
  });

const tireStatusFilter = () =>
  ({
    title: translateText('asset:tableColumnLabels.tireStatus'),
    id: 'tire-status',
    type: 'tireStatus',
    options: getTireConfig(),
  }) as FilterOptionConfig<ReportDiagnosticState, AssetListFilterCriteria>;

export const getAssetFluidConfig = (): FilterOption<ReportDiagnosticState>[] =>
  [ReportDiagnosticState.GOOD, ReportDiagnosticState.CRITICAL].map(
    (fluidStatus) => {
      return {
        label: translateText(
          `asset:filterModalLabels.fluidStatus.${fluidStatus}`
        ),
        value: fluidStatus,
      };
    }
  );

export const AVAILABLE_OIL_LIFE_OPTIONS = [
  OilLifeStatus.GOOD,
  OilLifeStatus.IMPAIRED,
  OilLifeStatus.CRITICAL,
];

export const getOilLifeConfig = (): FilterOption<OilLifeStatus>[] =>
  AVAILABLE_OIL_LIFE_OPTIONS.map((oilLifeStatus) => {
    return {
      label: getDiagnosticsFilterModalLabel(
        OIL_LIFE_FILTER_MODAL_STATUS,
        oilLifeStatus
      ),
      value: oilLifeStatus,
    };
  });

export const AVAILABLE_BRAKE_PAD_OPTIONS = [
  BrakePadStatus.GOOD,
  BrakePadStatus.IMPAIRED,
  BrakePadStatus.CRITICAL,
];

export const getBrakePadConfig = (): FilterOption<BrakePadStatus>[] =>
  AVAILABLE_BRAKE_PAD_OPTIONS.map((brakePadStatus) => {
    return {
      label: getDiagnosticModalLabel(
        BRAKE_PAD_FILTER_MODAL_STATUS,
        brakePadStatus
      ),
      value: brakePadStatus,
    };
  });

export const getDieselExhaustConfig =
  (): FilterOption<ReportDiagnosticState>[] =>
    [ReportDiagnosticState.GOOD, ReportDiagnosticState.CRITICAL].map(
      (status) => {
        return {
          label: translateText(
            `asset:filterModalLabels.dieselExhaustFluidDiagnosticStatus.${status}`
          ),
          value: status,
        };
      }
    );

const AVAILABLE_ENGINE_AIR_FILTER_OPTIONS = [
  ReportDiagnosticState.GOOD,
  ReportDiagnosticState.IMPAIRED,
  ReportDiagnosticState.CRITICAL,
];

const getEngineAirFilterConfig = (): FilterOption<ReportDiagnosticState>[] =>
  AVAILABLE_ENGINE_AIR_FILTER_OPTIONS.map((option) => {
    return {
      label: translateText(
        `asset:filterModalLabels.engineAirFilterStatus.${option}`
      ),
      value: option,
    };
  });

const engineAirFilter = () =>
  ({
    title: translateText('asset:vehicle.diagnostic.engineAirFilter'),
    id: 'engineAirFilter-status',
    type: 'engineAirFilterStatus',
    options: getEngineAirFilterConfig(),
  }) as FilterOptionConfig<ReportDiagnosticState, AssetListFilterCriteria>;

const washerFluidFilter = () =>
  ({
    title: translateText('asset:tableColumnLabels.washerFluidStatus'),
    id: 'washer-fluid-status',
    type: 'washerFluidStatus',
    options: getAssetFluidConfig(),
  }) as FilterOptionConfig<ReportDiagnosticState, AssetListFilterCriteria>;

const brakeFluidFilter = () =>
  ({
    title: translateText('asset:tableColumnLabels.brakeFluidStatus'),
    id: 'brake-fluid-status',
    type: 'brakeFluidStatus',
    options: getAssetFluidConfig(),
  }) as FilterOptionConfig<ReportDiagnosticState, AssetListFilterCriteria>;

const brakePadFrontFilter = () =>
  ({
    title: translateText('asset:brakePadFront'),
    id: 'brakePadFront-status',
    type: 'brakePadFrontStatus',
    options: getBrakePadConfig(),
  }) as FilterOptionConfig<BrakePadStatus, AssetListFilterCriteria>;

const brakePadRearFilter = () =>
  ({
    title: translateText('asset:brakePadRear'),
    id: 'brakePadRear-status',
    type: 'brakePadRearStatus',
    options: getBrakePadConfig(),
  }) as FilterOptionConfig<BrakePadStatus, AssetListFilterCriteria>;

const oilLifeFilter = () =>
  ({
    title: translateText('asset:oilLife'),
    id: 'oilLife-status',
    type: 'oilLifeStatus',
    options: getOilLifeConfig(),
  }) as FilterOptionConfig<OilLifeStatus, AssetListFilterCriteria>;

const dieselExhaustFluidFilter = () =>
  ({
    title: translateText('asset:diagnosticMapping.diesel_exhaust'),
    id: 'diesel-exhaust-fluid-status',
    type: 'dieselExhaustFluidStatus',
    options: getDieselExhaustConfig(),
  }) as FilterOptionConfig<ReportDiagnosticState, AssetListFilterCriteria>;

export const AVAILABLE_LAST_COMMUNICATED_FILTER_OPTIONS = Object.values(
  LastCommunicatedStatus
);

export const getConnectivityConfig =
  (): FilterOption<LastCommunicatedStatus>[] =>
    AVAILABLE_LAST_COMMUNICATED_FILTER_OPTIONS.map((status) => {
      return {
        label: translateText(`common:propertyState.${status}`),
        value: status,
      };
    });

export const AVAILABLE_LAST_IGNITION_FILTER_OPTIONS =
  Object.values(LastIgnitionStatus);
export const getLastIgnitionConfig = (): FilterOption<LastIgnitionStatus>[] =>
  AVAILABLE_LAST_IGNITION_FILTER_OPTIONS.map((status) => {
    return {
      label: translateText(`common:propertyState.${status}`),
      value: status,
    };
  });

const connectivityStatusFilter = () =>
  ({
    title: translateText('common:lastCommunicated'),
    id: 'lastCommunicated-status',
    type: 'lastCommunicatedStatus',
    options: getConnectivityConfig(),
  }) as FilterOptionConfig<LastCommunicatedStatus, AssetListFilterCriteria>;

/** Last ignition filter: same options as Last communicated (Within the past 1 hr, Within a day, Within a week, More than a week). */
const lastIgnitionStatusFilter = () =>
  ({
    title: translateText('asset:tableColumnLabels.lastIgnition'),
    id: 'lastIgnition-status',
    type: 'lastIgnitionStatus',
    options: getLastIgnitionConfig(),
  }) as FilterOptionConfig<LastIgnitionStatus, AssetListFilterCriteria>;

const getCommonFilters =
  (): BDFilterModalOptionConfig<AssetListFilterCriteria>[] => {
    const filters: BDFilterModalOptionConfig<AssetListFilterCriteria>[] = [];
    filters.push(connectivityStatusFilter());
    return filters;
  };
const vehicleTypePropulsionConfig = (): FilterOption<VehiclePropulsion>[] =>
  [VehiclePropulsion.ICE, VehiclePropulsion.EV].map((type) => {
    return {
      label: translateText(`asset:vehicle.propulsion.${type.toLowerCase()}`),
      value: type,
    };
  });
const vehicleTypePropulsionFilter = () =>
  ({
    title: translateText('asset:vehicle.type'),
    id: 'propulsion-type',
    type: 'propulsionType',
    options: vehicleTypePropulsionConfig(),
  }) satisfies FilterOptionConfig<VehiclePropulsion, AssetListFilterCriteria>;

export const getAdvisorFilterModalConfig =
  (): BDFilterModalOptionConfig<AssetListFilterCriteria>[] => {
    return [vehicleTypePropulsionFilter()];
  };
export const getDiagnosticsFilterModalConfig = (
  isVehicleHealthEnabled: boolean
): BDFilterModalOptionConfig<AssetListFilterCriteria>[] => {
  return [
    vehicleTypePropulsionFilter(),
    diagnosticsFilter(isVehicleHealthEnabled),
    ...getCommonFilters(),
    fuelFilter(),
    batterySocFilter(),
    tireStatusFilter(),
    engineAirFilter(),
    washerFluidFilter(),
    brakeFluidFilter(),
    brakePadFrontFilter(),
    brakePadRearFilter(),
    oilLifeFilter(),
    dieselExhaustFluidFilter(),
  ];
};
